import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@/hooks/useResponsive';
import { apiRequest } from '@/hooks/authUtils';
import { Input, Button, Spin } from 'antd';
import { SendOutlined, CloseOutlined, ClearOutlined } from '@ant-design/icons';

// 从环境变量读取是否显示 AI Bot（默认不显示）
const SHOW_AI_BOT = import.meta.env.VITE_SHOW_AI_BOT === 'true';

// 如果禁用，组件不渲染任何内容
const AIDisabled: React.FC = () => null;

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'loading';
  content: string;
}

interface ChatResponse {
  reply: string;
  conversationId: string;
  chatId: string;
}

const AIChatBot: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const conversationRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const welcomeMsg: Message = {
    id: 'welcome',
    role: 'assistant',
    content: t('chatBot.welcome'),
  };

  // Auto scroll to bottom
  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Focus input when opened
  useEffect(() => {
    if (open && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [open]);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || loading) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
    };

    const loadingMsg: Message = {
      id: 'loading',
      role: 'loading',
      content: '',
    };

    setMessages((prev) => {
      const isFirst = prev.length === 0;
      return [...(isFirst ? [welcomeMsg] : prev), userMsg, loadingMsg];
    });
    setInputValue('');
    setLoading(true);

    try {
      const data = await apiRequest<ChatResponse>('/api/chat/message', {
        method: 'POST',
        body: JSON.stringify({
          message: text,
          conversationId: conversationRef.current,
        }),
      });

      conversationRef.current = data.conversationId;

      setMessages((prev) =>
        prev.map((m) =>
          m.id === 'loading' ? { id: `ai-${Date.now()}`, role: 'assistant', content: data.reply } : m
        )
      );
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === 'loading'
            ? { id: `error-${Date.now()}`, role: 'assistant', content: t('chatBot.error') }
            : m
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    conversationRef.current = null;
    setMessages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Collapsed state: floating button
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#0ea5e9',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(14,165,233,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          zIndex: 9999,
          transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        aria-label="AI Chat"
      >
        💬
      </button>
    );
  }

  // Expanded: chat window
  if (isMobile) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 16, color: '#0ea5e9' }}>
            {t('chatBot.title')}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              icon={<ClearOutlined />}
              onClick={handleClear}
            >
              {t('chatBot.clearConversation')}
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
            />
          </div>
        </div>
        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {renderMessages()}
          <div ref={messagesEndRef} />
        </div>
        {/* Input */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: 12,
            background: '#fff',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Input.TextArea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatBot.placeholder')}
            autoSize={{ minRows: 1, maxRows: 4 }}
            style={{ flex: 1, resize: 'none' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={loading}
            style={{ background: '#0ea5e9', borderColor: '#0ea5e9' }}
          >
            {t('chatBot.send')}
          </Button>
        </div>
      </div>
    );
  }

  // Desktop: floating window
  return (
    <>
      {/* Chat window */}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 380,
          height: 520,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
            color: '#fff',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 15 }}>{t('chatBot.title')}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              onClick={handleClear}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                height: 28,
                fontSize: 12,
              }}
              icon={<ClearOutlined style={{ fontSize: 12 }} />}
            >
              {t('chatBot.clearConversation')}
            </Button>
            <Button
              size="small"
              onClick={() => setOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                height: 28,
              }}
              icon={<CloseOutlined />}
            />
          </div>
        </div>
        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
            background: '#fafafa',
          }}
        >
          {renderMessages()}
          <div ref={messagesEndRef} />
        </div>
        {/* Input */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '12px 16px',
            background: '#fff',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Input.TextArea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatBot.placeholder')}
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{ flex: 1, resize: 'none' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={loading}
            style={{ background: '#0ea5e9', borderColor: '#0ea5e9', minWidth: 64 }}
          >
            {t('chatBot.send')}
          </Button>
        </div>
      </div>
    </>
  );

  // Shared message rendering
  function renderMessages() {
    if (messages.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#999',
            fontSize: 14,
          }}
        >
          {welcomeMsg.content}
        </div>
      );
    }

    return messages.map((msg) => {
      if (msg.role === 'loading') {
        return (
          <div
            key={msg.id}
            style={{ display: 'flex', marginBottom: 12, alignItems: 'flex-start' }}
          >
            <span style={{ marginRight: 8, fontSize: 18 }}>🤖</span>
            <div
              style={{
                background: '#fff',
                borderRadius: '0 12px 12px 12px',
                padding: '10px 14px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              <Spin size="small" />{' '}
              <span style={{ color: '#999', fontSize: 13 }}>{t('chatBot.thinking')}</span>
            </div>
          </div>
        );
      }

      const isUser = msg.role === 'user';
      return (
        <div
          key={msg.id}
          style={{
            display: 'flex',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            marginBottom: 12,
            alignItems: 'flex-start',
          }}
        >
          {!isUser && <span style={{ marginRight: 8, fontSize: 18 }}>🤖</span>}
          <div
            style={{
              background: isUser ? '#0ea5e9' : '#fff',
              color: isUser ? '#fff' : '#333',
              borderRadius: isUser ? '12px 0 12px 12px' : '0 12px 12px 12px',
              padding: '10px 14px',
              maxWidth: '75%',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              fontSize: 14,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {msg.content}
          </div>
          {isUser && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 18,
                background: '#0ea5e9',
                borderRadius: '50%',
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              👤
            </span>
          )}
        </div>
      );
    });
  }
};

// 根据环境变量决定是否渲染
export default SHOW_AI_BOT ? AIChatBot : AIDisabled;
